import React, { useEffect, Suspense, lazy } from 'react';

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { injectStyle } from 'react-toastify/dist/inject-style';

import PrivateRoute from 'components/PrivateRoute';
import PreloadProgressBar from 'components/PreloadProgressBar';
import Auth from 'services/Auth';
import {
  firebaseAuthenticate,
  checkAndReFetchFirebaseAuth,
} from 'services/Firebase';
import { googleTagManager } from 'services/GoogleAnalytics';
import LocalStorageService from 'services/LocalStorageService';
import i18n from 'locales/i18n';
import './App.scss';
import PasswordRecovery from 'views/LoginPage/PasswordRecovery';
import VerifyEmailPage from 'views/LoginPage/VerifyEmailPage';

const DashboardPage = lazy(() => import('views/DashboardPage'));
const LoginPage = lazy(() => import('views/LoginPage'));
const ChangePasswordPage = lazy(() => import('views/ChangePasswordPage'));
const SetPasswordPage = lazy(() => import('views/SetPasswordPage'));

export default function App() {
  useEffect(() => {
    const initializeLanguageAndTranslation = () => {
      const userData = LocalStorageService.getUserData();
      const userLanguage = userData?.user?.defaultLanguage?.code;
      const storedLanguage = localStorage.getItem('appLanguage');

      let languageToUse = 'en';
      if (userLanguage) {
        languageToUse = userLanguage.toLowerCase();
      } else if (storedLanguage) {
        // Use previously stored language preference
        languageToUse = storedLanguage.toLowerCase();
      } else {
        const browserLang = (navigator.language || 'en')
          .split('-')[0]
          .toLowerCase();

        const supportedLanguages = [
          'en',
          'nl',
          'de',
          'fr',
          'it',
          'es',
          'pt',
          'bg',
        ];
        if (supportedLanguages.includes(browserLang)) {
          languageToUse = browserLang;
        }
      }

      // Store the language preference
      localStorage.setItem('appLanguage', languageToUse);
      i18n.changeLanguage(languageToUse.toUpperCase());

      const existingMeta = document.querySelector(
        'meta[name="google"][content="notranslate"]'
      );
      if (!existingMeta) {
        const meta = document.createElement('meta');
        meta.name = 'google';
        meta.content = 'notranslate';
        document.head.appendChild(meta);
      }

      const html = document.documentElement;
      html.setAttribute('translate', 'no');
      html.setAttribute('lang', languageToUse);
      document.body.setAttribute('translate', 'no');
    };

    initializeLanguageAndTranslation();
  }, []);

  useEffect(() => {
    const isIos = [
      'MacIntel',
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform);
    if (isIos) {
      const html = document.querySelector('html');
      html.classList.add('ios-polyfill');
    }
  }, []);
  const isAuth = Auth.isAuthenticated();

  useEffect(() => {
    injectStyle();
    googleTagManager();
    if (
      window.location.href.includes('dev.arion.aviapartner.aero') ||
      window.location.href.includes('uat.arion.aviapartner.aero')
    ) {
      let meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex';
      document.head.appendChild(meta);
    }
  }, []);

  useEffect(() => {
    if (isAuth) {
      firebaseAuthenticate();
      checkAndReFetchFirebaseAuth();
    }
  }, [isAuth]);

  useEffect(() => {
    const storageFile = LocalStorageService.getVersionFileToken();
    const fetchVersionFile = async () => {
      const response = await fetch(
        `${process.env.PUBLIC_URL}/version.json?nocache=${Math.random()}`
      );
      const file = await response.json();
      if (file?.token && file?.token !== storageFile) {
        LocalStorageService.setVersionFileToken(file.token);
        caches
          .keys()
          .then((keys) => {
            return Promise.all(keys.map((key) => caches.delete(key)));
          })
          .catch((err) => {
            console.warn('unregister caches error', err);
          });
        window.location.reload(true);
      }
    };
    fetchVersionFile();
  }, []);

  return (
    <>
      <Router>
        <Suspense fallback={<PreloadProgressBar />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/password-recovery" element={<PasswordRecovery />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/recovery/email/verify"
              element={<VerifyEmailPage />}
            />
            <Route
              path="/profile/recovery/email/verify"
              element={<VerifyEmailPage />}
            />
            <Route
              path="/*"
              element={
                <PrivateRoute>
                  <DashboardPage />
                </PrivateRoute>
              }
            />
          </Routes>
        </Suspense>
      </Router>
      <ToastContainer position="bottom-left" closeOnClick />
    </>
  );
}
